/**
 * RV Profile Service
 * Provides vehicle-specific characteristics and scoring adjustments
 */

import { w } from './market.js';
import * as units from './units.js';

export class RVProfileService {
    /**
     * Get RV type characteristics
     */
    static getRVProfile(rvType) {
        const profiles = {
            'class-a': {
                name: w('Class A Motorhome'),
                description: 'Large bus-style motorhome',
                windSensitivity: 1.3,      // Very sensitive to wind (tall, flat sides)
                gradeSensitivity: 1.2,     // Heavy, needs more power on grades
                elevationSensitivity: 1.3, // Large engine affected by altitude
                lengthFactor: 1.2,         // Long wheelbase, tight turns difficult
                typical: {
                    height: 12,
                    weight: 25000,
                    length: 35
                }
            },
            'class-b': {
                name: w('Class B Motorhome'),
                description: 'Van-style camper',
                windSensitivity: 0.7,      // Less wind impact (lower profile)
                gradeSensitivity: 0.6,     // Lighter, better power-to-weight
                elevationSensitivity: 0.7, // Smaller engine, less affected
                lengthFactor: 0.6,         // Short, very maneuverable
                typical: {
                    height: 9,
                    weight: 8000,
                    length: 20
                }
            },
            'class-c': {
                name: w('Class C Motorhome'),
                description: 'Cab-over style motorhome',
                windSensitivity: 1.0,      // Moderate wind sensitivity (baseline)
                gradeSensitivity: 1.0,     // Moderate grade performance (baseline)
                elevationSensitivity: 1.0, // Moderate elevation impact (baseline)
                lengthFactor: 1.0,         // Moderate maneuverability (baseline)
                typical: {
                    height: 11,
                    weight: 15000,
                    length: 28
                }
            },
            'fifth-wheel': {
                name: 'Fifth Wheel Trailer',
                description: 'Large trailer with gooseneck hitch',
                windSensitivity: 1.4,      // Very wind sensitive (tall, trailer sway)
                gradeSensitivity: 1.4,     // Heavy, depends on tow vehicle power
                elevationSensitivity: 1.2, // Tow vehicle affected by altitude
                lengthFactor: 1.4,         // Very long, difficult turns
                typical: {
                    height: 13,
                    weight: 12000,
                    length: 35
                }
            },
            'travel-trailer': {
                name: 'Travel Trailer',
                description: 'Bumper-pull trailer',
                windSensitivity: 1.5,      // Most wind sensitive (trailer sway)
                gradeSensitivity: 1.1,     // Moderate, depends on tow vehicle
                elevationSensitivity: 1.0, // Tow vehicle handles elevation
                lengthFactor: 1.1,         // Moderate length
                typical: {
                    height: 11,
                    weight: 7000,
                    length: 25
                }
            },
            'truck-camper': {
                name: 'Truck Camper',
                description: 'Camper mounted in truck bed',
                windSensitivity: 0.9,      // Moderate wind (high center of gravity)
                gradeSensitivity: 0.8,     // Good power-to-weight ratio
                elevationSensitivity: 0.8, // Truck engine handles altitude well
                lengthFactor: 0.5,         // Very short, highly maneuverable
                typical: {
                    height: 11,
                    weight: 4000,
                    length: 20
                }
            },
            'truck-only': {
                name: 'Truck Only',
                description: 'Tow vehicle without trailer',
                windSensitivity: 0.5,      // Minimal wind impact
                gradeSensitivity: 0.4,     // Excellent grade performance
                elevationSensitivity: 0.6, // Good altitude performance
                lengthFactor: 0.4,         // Very maneuverable
                typical: {
                    height: 7,
                    weight: 6000,
                    length: 20
                }
            }
        };

        return profiles[rvType] || profiles['class-c']; // Default to Class C
    }

    /**
     * Calculate adjusted penalties based on RV type and specs
     */
    static getAdjustedPenalties(rvType, rvSpecs, routeStats) {
        const profile = this.getRVProfile(rvType);
        const penalties = {
            wind: 0,
            grade: 0,
            elevation: 0,
            distance: 0
        };

        // For trailers, adjust based on tow vehicle capabilities
        let towVehicleMultiplier = 1.0;
        if (rvSpecs.towVehicle && rvSpecs.towVehicle.type) {
            towVehicleMultiplier = this.getTowVehicleMultiplier(rvSpecs.towVehicle, rvSpecs.weight);
        }

        // Wind penalty adjustment
        if (routeStats.avgWindSpeed) {
            const baseWindPenalty = routeStats.avgWindSpeed * 2;
            penalties.wind = baseWindPenalty * profile.windSensitivity;
            
            // Additional penalty for tall RVs in high wind
            if (rvSpecs.height > 12 && routeStats.avgWindSpeed > 15) {
                penalties.wind += 10; // Extra penalty for tall RVs in high wind
            }
            
            // Reduce wind penalty if trailer has sway control
            if (rvSpecs.towVehicle && rvSpecs.towVehicle.hasSwayControl) {
                penalties.wind *= 0.8; // 20% reduction with sway control
            }
        }

        // Grade penalty adjustment
        if (routeStats.maxGrade) {
            let baseGradePenalty = 0;
            if (routeStats.maxGrade > 8) baseGradePenalty = 30;
            else if (routeStats.maxGrade > 6) baseGradePenalty = 20;
            else if (routeStats.maxGrade > 4) baseGradePenalty = 10;
            else if (routeStats.maxGrade > 3) baseGradePenalty = 3;
            
            penalties.grade = baseGradePenalty * profile.gradeSensitivity * towVehicleMultiplier;
            
            // Heavy RVs get extra penalty on steep grades
            if (rvSpecs.weight > 20000 && routeStats.maxGrade > 6) {
                penalties.grade += 10; // Extra penalty for heavy RVs on steep grades
            }
            
            // Check towing capacity vs trailer weight
            if (rvSpecs.towVehicle && rvSpecs.towVehicle.capacity) {
                const capacityRatio = rvSpecs.weight / rvSpecs.towVehicle.capacity;
                if (capacityRatio > 0.9) {
                    // Towing near max capacity - extra penalty on grades
                    penalties.grade += 15;
                } else if (capacityRatio > 0.75) {
                    penalties.grade += 8;
                }
            }
        }

        // Elevation penalty adjustment
        if (routeStats.maxElevation) {
            let baseElevationPenalty = 0;
            if (routeStats.maxElevation > 11000) baseElevationPenalty = 25;
            else if (routeStats.maxElevation > 10000) baseElevationPenalty = 20;
            else if (routeStats.maxElevation > 9000) baseElevationPenalty = 15;
            else if (routeStats.maxElevation > 8000) baseElevationPenalty = 10;
            else if (routeStats.maxElevation > 7000) baseElevationPenalty = 5;
            
            penalties.elevation = baseElevationPenalty * profile.elevationSensitivity * towVehicleMultiplier;
            
            // Large engines (Class A, Fifth Wheel) more affected by altitude
            if ((rvType === 'class-a' || rvType === 'fifth-wheel') && routeStats.maxElevation > 10000) {
                penalties.elevation += 5; // Extra penalty for large engines at high altitude
            }
            
            // Diesel engines handle altitude better
            if (rvSpecs.towVehicle && rvSpecs.towVehicle.engine === 'diesel') {
                penalties.elevation *= 0.85; // 15% reduction for diesel
            }
        }

        // Steep grade count penalty
        if (routeStats.steepGradeCount) {
            penalties.grade += (routeStats.steepGradeCount * 3) * profile.gradeSensitivity * towVehicleMultiplier;
            
            // Trailer brakes help on descents
            if (rvSpecs.towVehicle && rvSpecs.towVehicle.hasTrailerBrakes) {
                penalties.grade *= 0.9; // 10% reduction with trailer brakes
            }
        }

        // Distance penalty (longer RVs prefer shorter routes)
        if (routeStats.distance) {
            const baseDistancePenalty = Math.min(routeStats.distance / 20, 30);
            penalties.distance = baseDistancePenalty * profile.lengthFactor;
        }

        return {
            penalties,
            profile,
            towVehicleMultiplier,
            totalPenalty: penalties.wind + penalties.grade + penalties.elevation + penalties.distance
        };
    }

    /**
     * Get tow vehicle capability multiplier
     * Returns a multiplier for grade/elevation penalties based on tow vehicle power
     */
    static getTowVehicleMultiplier(towVehicle, trailerWeight) {
        let multiplier = 1.0;
        
        // Base multiplier by vehicle type
        const vehicleMultipliers = {
            'heavy-duty': 0.6,      // F-450+, very capable
            'one-ton': 0.7,         // F-350, very strong
            'three-quarter-ton': 0.8, // F-250, strong
            'half-ton': 1.0,        // F-150, adequate
            'suv-large': 1.1,       // Expedition, less ideal
            'suv-mid': 1.3          // 4Runner, not ideal for heavy trailers
        };
        
        multiplier = vehicleMultipliers[towVehicle.type] || 1.0;
        
        // Adjust for engine type
        const engineMultipliers = {
            'diesel': 0.85,         // Best for towing
            'turbo': 0.9,           // Good power
            'gas-v8': 1.0,          // Standard
            'gas-v6': 1.15          // Less power
        };
        
        multiplier *= (engineMultipliers[towVehicle.engine] || 1.0);
        
        // Adjust for capacity vs weight ratio
        if (towVehicle.capacity && trailerWeight) {
            const ratio = trailerWeight / towVehicle.capacity;
            if (ratio > 0.9) {
                multiplier *= 1.3; // Towing at max capacity
            } else if (ratio > 0.75) {
                multiplier *= 1.15; // Towing heavy
            } else if (ratio < 0.5) {
                multiplier *= 0.9; // Plenty of capacity
            }
        }
        
        return multiplier;
    }

    /**
     * Get recommendations based on RV type
     */
    static getRecommendations(rvType, rvSpecs) {
        const profile = this.getRVProfile(rvType);
        const recommendations = [];

        // Wind recommendations
        if (profile.windSensitivity > 1.2) {
            recommendations.push({
                type: 'wind',
                severity: 'high',
                message: `${profile.name} is very sensitive to wind. Avoid routes with high wind speeds.`
            });
        }

        // Grade recommendations
        if (profile.gradeSensitivity > 1.2) {
            recommendations.push({
                type: 'grade',
                severity: 'high',
                message: `${profile.name} may struggle on steep grades. Prefer routes with gentler terrain.`
            });
        }

        // Elevation recommendations
        if (profile.elevationSensitivity > 1.2) {
            recommendations.push({
                type: 'elevation',
                severity: 'high',
                message: `${profile.name} engines lose significant power at high elevation. Avoid mountain passes when possible.`
            });
        }

        // Length recommendations
        if (profile.lengthFactor > 1.2) {
            recommendations.push({
                type: 'length',
                severity: 'medium',
                message: `${profile.name} requires extra space for turns and parking. Plan stops carefully.`
            });
        }

        // Height recommendations
        if (rvSpecs.height > 12) {
            recommendations.push({
                type: 'clearance',
                severity: 'high',
                message: `Your RV is ${units.isMetric() ? units.height(rvSpecs.height, 'up').toFixed(1) + ' m' : +Number(rvSpecs.height).toFixed(2) + ' ft'} tall. Always check bridge and tunnel clearances.`
            });
        }

        // Weight recommendations
        if (rvSpecs.weight > 25000) {
            recommendations.push({
                type: 'weight',
                severity: 'medium',
                message: `Your RV weighs ${units.isMetric() ? units.weight(rvSpecs.weight).toLocaleString('en-AU') + ' kg' : Math.round(Number(rvSpecs.weight)).toLocaleString() + ' lbs'}. Steep grades will be challenging.`
            });
        }

        return recommendations;
    }
}
